// Gift advisor — scripted quiz engine over live catalog data.
// Pure functions so the flow is unit-testable, and the UI (ChatbotWidget)
// only depends on getReply()'s shape. To upgrade to a real LLM later,
// swap getReply with a call to a Supabase Edge Function returning the
// same { messages, options, recommendations, state } shape.

import { effectivePrice } from './format';

const T = {
  vi: {
    hello: 'Xin chào! Mình là trợ lý quà tặng của Life in Nha Trang 🌊 Bạn đang tìm quà cho ai?',
    recipient: { self: 'Cho bản thân', family: 'Gia đình / bố mẹ', partner: 'Người yêu / vợ chồng', friends: 'Bạn bè', colleagues: 'Đồng nghiệp / công ty' },
    people: 'Món quà này dành cho khoảng bao nhiêu người dùng?',
    peopleOpts: { 1: '1 người', 3: '2–3 người', 5: '4–6 người', 8: 'Trên 6 người' },
    budget: 'Ngân sách của bạn khoảng bao nhiêu?',
    budgetOpts: { 200000: 'Dưới 300k', 650000: '300k – 1 triệu', 1500000: '1 – 2 triệu', 3000000: 'Trên 2 triệu' },
    interest: 'Người nhận thích kiểu quà nào?',
    interestOpts: { health: 'Sức khoẻ (yến, trầm, tinh dầu)', food: 'Ẩm thực đặc sản', souvenir: 'Lưu niệm & thời trang', any: 'Gợi ý giúp mình!' },
    reco: 'Dựa trên nhu cầu của bạn, đây là gợi ý phù hợp nhất 🎁',
    recoNone: 'Chưa có sản phẩm khớp hoàn toàn ngân sách, nhưng đây là các lựa chọn gần nhất:',
    again: 'Tư vấn lại từ đầu',
    comboTag: 'COMBO',
    qtyNote: (n) => `Gợi ý mua ${n} phần cho nhóm của bạn`,
  },
  en: {
    hello: "Hi! I'm the Life in Nha Trang gift advisor 🌊 Who are you shopping for?",
    recipient: { self: 'Myself', family: 'Family / parents', partner: 'Partner / spouse', friends: 'Friends', colleagues: 'Colleagues / company' },
    people: 'About how many people will use this gift?',
    peopleOpts: { 1: '1 person', 3: '2–3 people', 5: '4–6 people', 8: 'More than 6' },
    budget: "What's your budget?",
    budgetOpts: { 200000: 'Under 300k₫', 650000: '300k – 1M₫', 1500000: '1 – 2M₫', 3000000: 'Over 2M₫' },
    interest: 'What kind of gift do they enjoy?',
    interestOpts: { health: 'Wellness (bird’s nest, agarwood, oils)', food: 'Local food specialties', souvenir: 'Souvenirs & fashion', any: 'Surprise me!' },
    reco: 'Based on your answers, here are my top picks 🎁',
    recoNone: 'Nothing fits the budget exactly, but these are the closest picks:',
    again: 'Start over',
    comboTag: 'COMBO',
    qtyNote: (n) => `Suggested quantity: ${n} for your group`,
  },
};

const INTEREST_CATS = {
  health: ['Yến sào', 'Trầm hương', 'Mỹ phẩm thiên nhiên'],
  food: ['Đặc sản'],
  souvenir: ['Lưu niệm', 'Thời trang'],
  any: null,
};

const COMBO_KEYWORDS = { health: 'Sức Khoẻ', food: 'Đặc Sản', souvenir: 'Kỷ Niệm', partner: 'Nàng' };

export const initialState = { step: 'recipient', answers: {} };

export function recommend(answers, catalog) {
  const budget = Number(answers.budget) || 650000;
  const people = Number(answers.people) || 1;
  const cats = INTEREST_CATS[answers.interest] ?? null;

  const combos = (catalog.combos || [])
    .map((c) => {
      let score = 0;
      const price = effectivePrice(c);
      if (price <= budget * 1.15) score += 3;
      if (price <= budget) score += 1;
      const kw = COMBO_KEYWORDS[answers.interest];
      if (kw && c.name.includes(kw)) score += 2;
      if (answers.recipient === 'partner' && c.name.includes(COMBO_KEYWORDS.partner)) score += 3;
      return { kind: 'combo', item: c, score, price };
    })
    .filter((x) => x.score >= 3)
    .sort((a, b) => b.score - a.score || b.price - a.price)
    .slice(0, 2);

  let pool = (catalog.products || []).filter((p) => (cats ? cats.includes(p.category) : true));
  let scored = pool
    .map((p) => {
      const price = effectivePrice(p);
      let score = 0;
      if (price <= budget) score += 3;
      if (p.is_best_seller) score += 2;
      if (p.is_favorite) score += 1;
      if (p.is_hot_seasonal) score += 1;
      // Prefer per-person friendly prices for groups
      if (people > 1 && price <= budget / people) score += 2;
      return { kind: 'product', item: p, score, price };
    })
    .filter((x) => x.score >= 3)
    .sort((a, b) => b.score - a.score || b.price - a.price)
    .slice(0, 3);

  let exact = true;
  if (combos.length === 0 && scored.length === 0) {
    exact = false;
    scored = pool
      .map((p) => ({ kind: 'product', item: p, price: effectivePrice(p), score: 0 }))
      .sort((a, b) => a.price - b.price)
      .slice(0, 3);
  }

  const qty = people > 1 && answers.interest === 'food' ? Math.min(people, 10) : 1;
  return { picks: [...combos, ...scored].map((x) => ({ ...x, qty: x.kind === 'product' ? qty : 1 })), exact };
}

// Returns { state, messages: [], options: [{value,label}], recommendations?, done? }
export function getReply(state, choice, catalog, lang = 'vi') {
  const tt = T[lang] || T.vi;
  const opts = (obj) => Object.entries(obj).map(([value, label]) => ({ value, label }));

  if (!state || choice === 'restart') {
    return { state: { step: 'people_wait', answers: {} }, messages: [tt.hello], options: opts(tt.recipient) };
  }

  const answers = { ...state.answers };

  switch (state.step) {
    case 'recipient':
      return { state: { step: 'people_wait', answers }, messages: [tt.hello], options: opts(tt.recipient) };
    case 'people_wait':
      answers.recipient = choice;
      return { state: { step: 'budget_wait', answers }, messages: [tt.people], options: opts(tt.peopleOpts) };
    case 'budget_wait':
      answers.people = choice;
      return { state: { step: 'interest_wait', answers }, messages: [tt.budget], options: opts(tt.budgetOpts) };
    case 'interest_wait':
      answers.budget = choice;
      return { state: { step: 'reco_wait', answers }, messages: [tt.interest], options: opts(tt.interestOpts) };
    case 'reco_wait': {
      answers.interest = choice;
      const { picks, exact } = recommend(answers, catalog);
      const msgs = [exact ? tt.reco : tt.recoNone];
      const groupQty = picks.find((p) => p.qty > 1)?.qty;
      if (groupQty) msgs.push(tt.qtyNote(groupQty));
      return {
        state: { step: 'done', answers },
        messages: msgs,
        options: [{ value: 'restart', label: tt.again }],
        recommendations: picks,
        done: true,
      };
    }
    default:
      return { state: { step: 'people_wait', answers: {} }, messages: [tt.hello], options: opts(tt.recipient) };
  }
}
